import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Swords, Settings, LogOut, Trophy, X, Moon, Sun, Globe, BookOpen, FileSpreadsheet, Gauge, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLadder } from '../contexts/LadderContext';
import { Button } from './ui/button';

// Phidél branding component
const PhidelBadge = ({ text = "Admin Panel" }) => (
  <span className="phidel-badge">
    {text}
  </span>
);

export const AdminSidebar = ({ isOpen, onClose }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { logout, isSuperAdmin } = useAuth();
  const { ladder } = useLadder();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    // Available to all admins
    { path: '/admin', label: t('dashboard'), icon: LayoutDashboard, exact: true },
    { path: '/admin/players', label: t('players'), icon: Users },
    { path: '/admin/matches', label: t('matchEntry'), icon: Swords },
    // Super Admin only
    ...(isSuperAdmin ? [
      { path: '/admin/settings', label: t('settings'), icon: Settings },
      { path: '/admin/translations', label: t('translations'), icon: Globe },
      { path: '/admin/rules-editor', label: language === 'el' ? 'Κανόνες' : 'Rules', icon: BookOpen },
      { path: '/admin/phidel-level-editor', label: language === 'el' ? 'Phidél Level' : 'Phidél Level', icon: Gauge },
      { path: '/admin/export', label: t('exportData'), icon: FileSpreadsheet },
      { path: '/admin/admins', label: language === 'el' ? 'Admins' : 'Admins', icon: ShieldCheck }
    ] : [])
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={onClose}
        />
      )}

      <aside className={`admin-sidebar ${isOpen ? 'open' : ''}`} data-testid="admin-sidebar">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="sidebar-logo">
              {ladder?.club_logo ? (
                <img 
                  src={ladder.club_logo} 
                  alt="Club Logo" 
                  className="h-10 w-10 object-contain rounded-lg"
                />
              ) : (
                <div className="h-10 w-10 bg-primary rounded-lg flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-primary-foreground" />
                </div>
              )}
              <div>
                <h1 className="font-heading font-bold text-foreground text-base leading-tight truncate max-w-[140px]">
                  {ladder?.name || 'Padel Ladder'}
                </h1>
                <p className="phidel-badge">Admin Panel</p>
              </div>
            </Link>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-muted"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ path, label, icon: Icon, exact }) => (
            <Link
              key={path}
              to={path}
              onClick={onClose}
              data-testid={`sidebar-${path.split('/').pop() || 'dashboard'}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive(path, exact)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon className="h-5 w-5" />
              {label}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border space-y-3">
          {/* Theme & Language Row */}
          <div className="flex items-center justify-center gap-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="theme-toggle"
              data-testid="admin-theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>

            {/* Language Toggle */}
            <div className="lang-toggle" data-testid="admin-language-toggle">
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                className={language === 'en' ? 'active' : ''}
              >
                EN
              </button>
              <button
                onClick={() => language !== 'el' && toggleLanguage()}
                className={language === 'el' ? 'active' : ''}
              >
                EL
              </button>
            </div>
          </div>

          {/* View Public Site */}
          <Link to="/" onClick={onClose}>
            <Button variant="outline" className="w-full justify-center gap-2" size="sm">
              <Trophy className="h-4 w-4" />
              {t('leaderboard')}
            </Button>
          </Link>

          {/* Logout */}
          <Button 
            variant="ghost" 
            className="w-full justify-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            {t('logout')}
          </Button>
        </div>
      </aside>
    </>
  );
};
